import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import useMunicipalitySettings from 'api/hooks/use-municipality-settings'

const isColorDark = (hex) => {
  const cleaned = hex.replace('#', '')
  if (cleaned.length !== 6) return false
  const r = parseInt(cleaned.slice(0, 2), 16)
  const g = parseInt(cleaned.slice(2, 4), 16)
  const b = parseInt(cleaned.slice(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance < 0.5
}

const FONT_SIZES = {
  sm: { title: '1rem', subtitle: '0.75rem' },
  md: { title: '1.25rem', subtitle: '0.9rem' },
  lg: { title: '1.75rem', subtitle: '1.1rem' }
}

const LOGO_SIZES = {
  xs: '24px',
  sm: '32px',
  md: '48px',
  lg: '64px',
  xl: '80px'
}

const ALIGN_MAP = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end'
}

const HeaderChart = ({
  title, subtitle, backgroundColor, showLogo, titleColor, subtitleColor,
  fontSize, textAlign, showDivider, logoSize, showDate
}) => {
  const { data: municipalitySettings } = useMunicipalitySettings({ options: {} })
  const logoUrl = municipalitySettings?.logo_url

  const bg = backgroundColor || '#ffffff'
  const isDark = isColorDark(bg)
  const sizes = FONT_SIZES[fontSize] || FONT_SIZES.md
  const logoHeight = LOGO_SIZES[logoSize] || LOGO_SIZES.md
  const align = textAlign || 'left'

  const resolvedTitleColor = titleColor || (isDark ? '#ffffff' : '#212529')
  const resolvedSubtitleColor = subtitleColor || (isDark ? 'rgba(255,255,255,0.75)' : '#6c757d')
  const displayLogo = showLogo !== false

  const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })

  return (
    <div>
      <div
        className="d-flex align-items-center gap-3 p-3 rounded"
        style={{
          backgroundColor: bg,
          minHeight: '80px',
          justifyContent: ALIGN_MAP[align],
          borderRadius: showDivider ? '0.375rem 0.375rem 0 0' : undefined
        }}
      >
        {displayLogo && logoUrl && align !== 'right' && (
          <img
            src={logoUrl}
            alt="Header logo"
            style={{ height: logoHeight, width: 'auto', objectFit: 'contain', flexShrink: 0 }}
          />
        )}
        <div style={{ textAlign: align }}>
          <div
            className="fw-bold"
            style={{ color: resolvedTitleColor, fontSize: sizes.title, lineHeight: 1.2 }}
          >
            {title}
          </div>
          {subtitle && (
            <div className="mt-1" style={{ color: resolvedSubtitleColor, fontSize: sizes.subtitle }}>
              {subtitle}
            </div>
          )}
          {showDate && (
            <div className="mt-1" style={{ color: resolvedSubtitleColor, fontSize: sizes.subtitle, fontStyle: 'italic' }}>
              As of {today}
            </div>
          )}
        </div>
        {displayLogo && logoUrl && align === 'right' && (
          <img
            src={logoUrl}
            alt="Header logo"
            style={{ height: logoHeight, width: 'auto', objectFit: 'contain', flexShrink: 0 }}
          />
        )}
      </div>
      {showDivider && (
        <hr style={{ margin: 0, borderColor: isDark ? 'rgba(255,255,255,0.3)' : '#dee2e6' }} />
      )}
    </div>
  )
}

HeaderChart.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  backgroundColor: PropTypes.string,
  showLogo: PropTypes.bool,
  titleColor: PropTypes.string,
  subtitleColor: PropTypes.string,
  fontSize: PropTypes.string,
  textAlign: PropTypes.string,
  showDivider: PropTypes.bool,
  logoSize: PropTypes.string,
  showDate: PropTypes.bool
}

// ---------------------------------------------------------------------------
// Option helpers
// ---------------------------------------------------------------------------

const getBrandColorOptions = (municipalitySettings) => {
  const options = [
    { label: 'White', value: '#ffffff' },
    { label: 'Light Gray', value: '#f8f9fa' }
  ]
  if (municipalitySettings) {
    ;[
      { key: 'color_1', label: 'Brand 1' },
      { key: 'color_2', label: 'Brand 2' },
      { key: 'color_3', label: 'Brand 3' },
      { key: 'color_4', label: 'Brand 4' },
      { key: 'color_5', label: 'Brand 5' }
    ].forEach(({ key, label }) => {
      if (municipalitySettings[key]) {
        options.push({ label, value: municipalitySettings[key] })
      }
    })
  }
  options.push({ label: 'Gray', value: '#808080' })
  options.push({ label: 'Dark', value: '#343a40' })
  options.push({ label: 'Black', value: '#212529' })
  return options
}

const InlineColorPicker = ({ colorOptions, selectedColor, onSelect, onReset, autoLabel }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const displayColor = selectedColor || '#dee2e6'

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        title={selectedColor || autoLabel || 'Auto'}
        style={{
          width: '28px',
          height: '28px',
          borderRadius: '4px',
          backgroundColor: displayColor,
          border: open ? '2px solid #0d6efd' : '1px solid #ced4da',
          cursor: 'pointer',
          padding: 0,
          flexShrink: 0
        }}
      />
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '32px',
            left: 0,
            zIndex: 10,
            backgroundColor: '#fff',
            border: '1px solid #dee2e6',
            borderRadius: '6px',
            padding: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            width: '180px'
          }}
        >
          {colorOptions.map(({ label, value }) => {
            const isSelected = selectedColor === value
            return (
              <button
                key={value}
                type="button"
                title={label}
                onClick={() => { onSelect(value); setOpen(false) }}
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  backgroundColor: value,
                  border: isSelected ? '2px solid #0d6efd' : '1px solid #ced4da',
                  cursor: 'pointer',
                  padding: 0
                }}
              />
            )
          })}
          {onReset && selectedColor && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 w-100 mt-1"
              style={{ fontSize: '0.7rem' }}
              onClick={() => { onReset(); setOpen(false) }}
            >
              Reset to auto
            </button>
          )}
        </div>
      )}
    </div>
  )
}

InlineColorPicker.propTypes = {
  colorOptions: PropTypes.array.isRequired,
  selectedColor: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onReset: PropTypes.func,
  autoLabel: PropTypes.string
}

const SegmentedControl = ({ options, value, onChange }) => (
  <div className="btn-group btn-group-sm" role="group">
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        className={`btn ${value === opt.value ? 'btn-primary' : 'btn-light'}`}
        onClick={() => onChange(opt.value)}
        title={opt.title || opt.label}
        style={{ minWidth: '36px' }}
      >
        {opt.label}
      </button>
    ))}
  </div>
)

SegmentedControl.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired
}

// ---------------------------------------------------------------------------
// Chart registry configuration
// ---------------------------------------------------------------------------
export const headerChartConfig = {
  requiredFields: ['title'],

  validate: (_formData) => ({}),

  isQueryEnabled: () => false,

  buildChartParams: (formData) => ({
    subtitle: formData.headerSubtitle || '',
    background_color: formData.headerBackgroundColor || '',
    show_logo: formData.headerShowLogo !== false,
    title_color: formData.headerTitleColor || '',
    subtitle_color: formData.headerSubtitleColor || '',
    font_size: formData.headerFontSize || 'md',
    text_align: formData.headerTextAlign || 'left',
    show_divider: formData.headerShowDivider || false,
    logo_size: formData.headerLogoSize || 'md',
    show_date: formData.headerShowDate || false,
    repeat_on_every_page: formData.headerRepeatOnEveryPage || false
  }),

  buildQueryParams: (baseParams) => baseParams,

  renderOptions: ({ Form: F, formState, setters, municipalitySettings, validationErrors, hasSubmitted }) => {
    const {
      title, headerSubtitle, headerBackgroundColor, headerShowLogo, headerTitleColor, headerSubtitleColor,
      headerFontSize, headerTextAlign, headerShowDivider, headerLogoSize, headerShowDate, headerRepeatOnEveryPage
    } = formState
    const {
      setTitle, setHeaderSubtitle, setHeaderBackgroundColor, setHeaderShowLogo, setHeaderTitleColor, setHeaderSubtitleColor,
      setValidationErrors, setHeaderFontSize, setHeaderTextAlign, setHeaderShowDivider, setHeaderLogoSize, setHeaderShowDate,
      setHeaderRepeatOnEveryPage
    } = setters
    const colorOptions = getBrandColorOptions(municipalitySettings)

    const bg = headerBackgroundColor || '#ffffff'
    const dark = isColorDark(bg)
    const sizes = FONT_SIZES[headerFontSize] || FONT_SIZES.md
    const logoHeight = LOGO_SIZES[headerLogoSize] || LOGO_SIZES.md
    const align = headerTextAlign || 'left'
    const previewTitleColor = headerTitleColor || (dark ? '#ffffff' : '#212529')
    const previewSubtitleColor = headerSubtitleColor || (dark ? 'rgba(255,255,255,0.75)' : '#6c757d')
    const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })

    const logoPlaceholder = (
      <div
        style={{
          width: logoHeight,
          height: logoHeight,
          borderRadius: '4px',
          backgroundColor: dark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: '0.55rem',
          color: dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)'
        }}
      >
        Logo
      </div>
    )

    return (
      <>
        <F.Group className="mb-3">
          <F.Label>Preview</F.Label>
          <div style={{ border: '1px solid #dee2e6', borderRadius: '6px', overflow: 'hidden' }}>
            <div
              className="d-flex align-items-center gap-3 p-3"
              style={{
                backgroundColor: bg,
                minHeight: '60px',
                justifyContent: ALIGN_MAP[align]
              }}
            >
              {headerShowLogo && align !== 'right' && logoPlaceholder}
              <div style={{ textAlign: align }}>
                <div className="fw-bold" style={{ color: previewTitleColor, fontSize: sizes.title, lineHeight: 1.2 }}>
                  {title || 'Title'}
                </div>
                {headerSubtitle && (
                  <div className="mt-1" style={{ color: previewSubtitleColor, fontSize: sizes.subtitle }}>
                    {headerSubtitle}
                  </div>
                )}
                {headerShowDate && (
                  <div className="mt-1" style={{ color: previewSubtitleColor, fontSize: sizes.subtitle, fontStyle: 'italic' }}>
                    As of {today}
                  </div>
                )}
              </div>
              {headerShowLogo && align === 'right' && logoPlaceholder}
            </div>
            {headerShowDivider && (
              <hr style={{ margin: 0, borderColor: dark ? 'rgba(255,255,255,0.3)' : '#dee2e6' }} />
            )}
          </div>
        </F.Group>

        <F.Group className="mb-3">
          <div className="d-flex align-items-center gap-2">
            <F.Label className="mb-0">Background Color</F.Label>
            <InlineColorPicker
              colorOptions={colorOptions}
              selectedColor={headerBackgroundColor}
              onSelect={setHeaderBackgroundColor}
            />
          </div>
        </F.Group>

        <F.Group className="mb-3">
          <div className="d-flex align-items-center gap-2">
            <F.Label className="mb-0">Title</F.Label>
            <InlineColorPicker
              colorOptions={colorOptions}
              selectedColor={headerTitleColor}
              onSelect={setHeaderTitleColor}
              onReset={() => setHeaderTitleColor('')}
              autoLabel="Auto from background"
            />
          </div>
          <F.Control
            size="sm"
            className="mt-1"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value)
              if (validationErrors?.title) {
                setValidationErrors((prev) => ({ ...prev, title: undefined }))
              }
            }}
            isInvalid={hasSubmitted && !!validationErrors?.title}
          />
          {hasSubmitted && validationErrors?.title && (
            <F.Control.Feedback type="invalid">{validationErrors.title}</F.Control.Feedback>
          )}
        </F.Group>
        <F.Group className="mb-3">
          <div className="d-flex align-items-center gap-2">
            <F.Label className="mb-0">Subtitle (optional)</F.Label>
            <InlineColorPicker
              colorOptions={colorOptions}
              selectedColor={headerSubtitleColor}
              onSelect={setHeaderSubtitleColor}
              onReset={() => setHeaderSubtitleColor('')}
              autoLabel="Auto from background"
            />
          </div>
          <F.Control
            size="sm"
            className="mt-1"
            type="text"
            value={headerSubtitle}
            onChange={(e) => setHeaderSubtitle(e.target.value)}
            placeholder="e.g., Fiscal Year 2025 Analytics"
          />
        </F.Group>

        <div className="row mb-3">
          <div className="col">
            <F.Label className="mb-1">Font Size</F.Label>
            <div>
              <SegmentedControl
                options={[
                  { value: 'sm', label: 'S' },
                  { value: 'md', label: 'M' },
                  { value: 'lg', label: 'L' }
                ]}
                value={headerFontSize || 'md'}
                onChange={setHeaderFontSize}
              />
            </div>
          </div>
          <div className="col">
            <F.Label className="mb-1">Alignment</F.Label>
            <div>
              <SegmentedControl
                options={[
                  { value: 'left', label: '\u2190', title: 'Left' },
                  { value: 'center', label: '\u2194', title: 'Center' },
                  { value: 'right', label: '\u2192', title: 'Right' }
                ]}
                value={headerTextAlign || 'left'}
                onChange={setHeaderTextAlign}
              />
            </div>
          </div>
        </div>

        <div className="row mb-3">
          <div className="col">
            <F.Check
              type="checkbox"
              label="Show logo"
              checked={headerShowLogo}
              onChange={(e) => setHeaderShowLogo(e.target.checked)}
            />
          </div>
          {headerShowLogo && (
            <div className="col">
              <F.Label className="mb-1">Logo Size</F.Label>
              <div>
                <SegmentedControl
                  options={[
                    { value: 'xs', label: 'XS' },
                    { value: 'sm', label: 'S' },
                    { value: 'md', label: 'M' },
                    { value: 'lg', label: 'L' },
                    { value: 'xl', label: 'XL' }
                  ]}
                  value={headerLogoSize || 'md'}
                  onChange={setHeaderLogoSize}
                />
              </div>
            </div>
          )}
        </div>

        <div className="mb-3">
          <F.Check
            type="checkbox"
            label="Show divider line"
            checked={headerShowDivider || false}
            onChange={(e) => setHeaderShowDivider(e.target.checked)}
            className="mb-2"
          />
          <F.Check
            type="checkbox"
            label={'Show date stamp ("As of ...)'}
            checked={headerShowDate || false}
            onChange={(e) => setHeaderShowDate(e.target.checked)}
            className="mb-2"
          />
          <F.Check
            type="checkbox"
            label="Repeat on every page in PDF export"
            checked={headerRepeatOnEveryPage || false}
            onChange={(e) => setHeaderRepeatOnEveryPage(e.target.checked)}
          />
        </div>

      </>
    )
  }
}

export default HeaderChart
